/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 이미지 목록을 목록형태로 변환하는 툴을 정의한다.
 *
 * @file /src/rootin/blocks/images/tools/Ratio.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 7. 19.
 */
import { Tool } from '@rootin/base/Tool';
import { Block } from '@rootin/base/Block';
import { Element } from '@rootin/base/Element';
import { Language, LanguageText } from '@utils/Language';
import { Menu } from '@rootin/core/Menu';
import { Group } from '@components/form/field/radio/Group';
import { Images } from '../Images';
import { Html } from '@utils/Html';

export class Ratio extends Tool {
    /**
     * 툴타입
     */
    static type: string = 'ratio';

    /**
     * 툴그룹
     */
    static group: string = 'images-options';

    /**
     * 툴아이콘
     */
    icon: string =
        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-proportions-icon lucide-proportions"><rect width="20" height="20" x="2" y="2" rx="2" ry="2.5"/><path d="M12 8.25v13.75"/><path d="M2 8.25h13a2 2.5 0 0 1 2 2.5v11.25"/></svg>';

    /**
     * 툴팁
     */
    text: LanguageText = Language.get('rootin.blocks.images.tools.ratio');

    /**
     * 메뉴
     */
    menu: Menu = null;

    /**
     * 메뉴를 연 이미지블록
     */
    block: Images = null;

    /**
     * 메뉴를 가져온다.
     *
     * @returns 메뉴
     */
    getMenu(): Menu {
        if (this.menu === null) {
            this.menu = new Menu({
                $target: this.$getButton(),
                menus: [
                    {
                        type: 'custom',
                        name: 'ratio',
                        custom: new Group({
                            displayType: 'box',
                            columns: 6,
                            className: 'ratio',
                            initOnChange: false,
                            options: {
                                'origin': '<i><i data-ratio="1:1"></i></i><span data-role="origin"></span>',
                                '1:1': '<i><i data-ratio="1:1"></i></i><span>1:1</span>',
                                '4:3': '<i><i data-ratio="4:3"></i></i><span>4:3</span>',
                                '3:4': '<i><i data-ratio="3:4"></i></i><span>3:4</span>',
                                '16:10': '<i><i data-ratio="16:9"></i></i><span>16:10</span>',
                                '10:16': '<i><i data-ratio="9:16"></i></i><span>10:16</span>',
                            },
                            listeners: {
                                change: (_, value) => {
                                    this.block.setRatio(value);
                                    this.menu.hide();
                                },
                            },
                        }),
                    },
                ],
                listeners: {
                    show: (menu) => {
                        this.editor.focus.suspend();
                        const $items = this.block.$getBlock();
                        const ratio = menu.getMenuByName('ratio', 'custom').custom as Group;
                        ratio.setValue($items.getAttr('data-ratio'), true);
                        this.$getButton().addClass('active');
                        Html.get('span[data-role=origin]', ratio.$getContent()).html(
                            Language.get('rootin.blocks.images.ratio.origin')
                        );
                    },
                    hide: () => {
                        this.editor.focus.resume();
                        this.$getButton().removeClass('menu');
                        this.editor.refreshTools();
                    },
                },
            });

            this.menu.$getComponent().on('mousedown', (_, e) => {
                e.preventDefault();
            });
        }

        return this.menu;
    }

    /**
     * 툴 클릭시 실행할 핸들러를 정의한다.
     */
    handler(): void {
        if (this.editor.focus.getBlock().getParents('images') !== null) {
            this.block = this.editor.focus.getBlock().getParents('images') as Images;
            this.getMenu().show(false);
        }
    }

    /**
     * 툴의 표시여부 조건을 설정한다.
     * @todo 커서가 편집 가능한 유닛 안이거나 다중 유닛이 선택돼 있을 때만 노출할지 고민한다.
     *
     * @param block - 커서가 위치한 블록
     * @param element - 커서가 위치한 편집유닛
     * @returns 표시여부
     */
    visible(block: Block | null, element: Element | null): boolean {
        return block.getParents('images') !== null;
    }

    /**
     * 툴이 활성여부 조건을 설정한다.
     *
     * @returns 활성여부
     */
    active(): boolean {
        return false;
    }

    /**
     * 툴의 표시/활성여부를 갱신한다.
     *
     * @param block - 커서가 위치한 블록
     * @param element - 커서가 위치한 편집유닛
     */
    refresh(block: Block | null, element: Element | null): void {
        super.refresh(block, element);
        if (this.visible(block, element) == false) {
            this.getMenu().hide();
        }
    }
}
